import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import {useFormik} from 'formik';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import API from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppDispatch, useAppSelector} from '../../hooks/redux';
import {
  ConfigurationInput,
  ConfigurationResponse,
} from '../../interfaces/Configuration';
import reduxActions from '../../redux/actions';
import {getMergedConfigurations} from '../../utils/configuration';
import {COLORED_ICONS} from '../../utils/icons';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import {ColorSelect} from '../selectors/ColorSelect';
import {SoundSelect} from '../selectors/SoundSelect';

interface ConfigurationBeltValues {
  [key: string]: string | number | null;
}

const FIELDS_BY_SECTIONS = {
  soundGlobal: [
    'belt-sound.belt_event_sound_global_new',
    'belt-sound.belt_event_sound_interval',
  ],

  sound: [
    'belt-sound.belt_event_sound_601',
    'belt-sound.belt_event_sound_602',
    'belt-sound.belt_event_sound_603',
    'belt-sound.belt_event_sound_604',
    'belt-sound.belt_event_sound_605',
    'belt-sound.belt_event_sound_606',
    'belt-sound.belt_event_sound_610',
  ],

  color: [
    'belt-color.belt_event_color_601',
    'belt-color.belt_event_color_602',
    'belt-color.belt_event_color_603',
    'belt-color.belt_event_color_604',
    'belt-color.belt_event_color_605',
    'belt-color.belt_event_color_606',
    'belt-color.belt_event_color_610',
  ],
};

const FIELDS = _.flatMap(FIELDS_BY_SECTIONS);

const getFormikValues = (
  item?: ConfigurationResponse | null
): ConfigurationBeltValues => ({
  ..._.chain(FIELDS)
    .map((name) => ({name, value: null}))
    .keyBy('name')
    .mapValues('value')
    .value(),

  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('value')
    .value(),
});

const getFormikValuesClear = (
  item?: ConfigurationResponse | null
): ConfigurationBeltValues => ({
  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('default')
    .value(),
});

export interface MyBeltConfigProps {
  disabled?: boolean;
  onChangeSubmittedInProgress?: (value: boolean) => any;
  onChangeResetInProgress?: (value: boolean) => any;
}

export interface MyBeltConfigRef {
  fetch?: Function;
  submit?: Function;
  reset?: Function;
  clear?: Function;
}

export const MyBeltConfig = forwardRef<
  MyBeltConfigRef,
  React.PropsWithChildren<MyBeltConfigProps>
>(({disabled, onChangeSubmittedInProgress, onChangeResetInProgress}, ref) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const myConfigurations = useAppSelector(({app}) => app.myConfigurations);

  /*******/
  /* ref */
  /*******/
  useImperativeHandle(ref, () => ({
    fetch: () => fetchData(),
    submit: () => formik.handleSubmit(),
    reset: () => handleResetData(),
    clear: () => formik.setValues(getFormikValuesClear(myConfigurations)),
  }));

  /*********/
  /* fetch */
  /*********/

  const [fetchedErrors, setFetchedErrors] = useState<string[]>();
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);

    try {
      const params = {section: JSON.stringify(['belt-general', 'belt-color', 'belt-sound'])};
      const resp = await API.get<ConfigurationResponse>(
        `${apiBaseUrl}/user-configuration`,
        {params}
      );
      reduxActions.app.setApp(reduxDispatch, {
        myConfigurations: getMergedConfigurations(myConfigurations, resp.data),
      });
    } catch (error: any) {
      setFetchedErrors(error.data);
    }

    setFetchedInProgress(false);
  };

  useEffect(() => {
    fetchData();

    return () => {
      setFetchedErrors(undefined);
    };
  }, []);

  /**********/
  /* submit */
  /**********/
  const [submittedInProgress, setSubmittedInProgress] = useState(false);
  const reduxDispatch = useAppDispatch();

  const submitData = async (data: ConfigurationInput) => {
    setSubmittedInProgress(true);

    try {
      const resp = await API.post<ConfigurationResponse>(
        `${apiBaseUrl}/user-configuration`,
        data
      );

      reduxActions.app.setApp(reduxDispatch, {
        myConfigurations: getMergedConfigurations(myConfigurations, resp.data),
      });
      enqueueSnackbar('Configuration successfully updated', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setSubmittedInProgress(false);
  };

  /**********/
  /* reset */
  /**********/
  const [resetInProgress, setResetInProgress] = useState(false);

  const resetData = async (data: ConfigurationInput) => {
    setResetInProgress(true);

    try {
      const resp = await API.patch<ConfigurationResponse>(
        `${apiBaseUrl}/user-configuration`,
        data
      );

      reduxActions.app.setApp(reduxDispatch, {
        myConfigurations: getMergedConfigurations(myConfigurations, resp.data),
      });
      enqueueSnackbar('Configuration successfully reset', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
    } catch (error: any) {
      // setSubmittedErrors(error.data);
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setResetInProgress(false);
  };

  const handleResetData = async () => {
    const input: ConfigurationInput = {
      section: ['belt-general', 'belt-sound', 'belt-color'],
      configuration: _.map(formik.values, (value, key) => ({
        section: key.split('.')[0],
        name: key.split('.')[1],
        value,
      })),
    };
    await resetData(input);
  };

  /*********/
  /* input */
  /*********/
  const inputValidationSchema = yup.object();

  const formik = useFormik({
    initialValues: getFormikValues(myConfigurations),
    validationSchema: inputValidationSchema,
    onSubmit: async (values) => {
      const input: ConfigurationInput = {
        section: ['belt-general', 'belt-color', 'belt-sound'],
        configuration: _.map(values, (value, key) => ({
          section: key.split('.')[0],
          name: key.split('.')[1],
          value,
        })),
      };
      await submitData(input);
    },
  });

  useEffect(() => {
    const newInput = getFormikValues(myConfigurations);
    if (!_.isEqual(newInput, formik.values)) {
      formik.setValues(newInput);
    }
  }, [myConfigurations]);

  /************/
  /* keysData */
  /************/
  const keysData = useMemo(
    () => ({
      ..._.chain(myConfigurations)
        .keyBy((i) => `${i.section}.${i.name}`)
        .mapValues()
        .value(),
    }),
    [myConfigurations]
  );

  // other
  useEffect(() => {
    onChangeSubmittedInProgress?.(submittedInProgress);
  }, [submittedInProgress]);

  useEffect(() => {
    onChangeResetInProgress?.(resetInProgress);
  }, [resetInProgress]);

  return (
    <Box position="relative">
      <Backdrop open={fetchedInProgress}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors?.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <Grid container spacing={4} mb={3}>
        <Grid item xs={12} lg={6} xl={8}>
          <Paper sx={{p: 3, mb: 3}}>
            <Box fontSize={20} lineHeight={1.6} mb={2}>
              BELT Sound
            </Box>

            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Global
            </Box>

            <Grid container spacing={4} mb={6}>
              <Grid item xs={6}>
                {FIELDS_BY_SECTIONS.soundGlobal.slice(0, 1).map((name) => (
                  <SoundSelect
                    key={name}
                    fullWidth
                    label={t(keysData[name]?.label)}
                    value={formik.values[name]}
                    name={name}
                    size="small"
                    disabled={disabled}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                ))}
              </Grid>

              <Grid item xs={6}>
                {FIELDS_BY_SECTIONS.soundGlobal.slice(1, 2).map((name) => (
                  <TextField
                    key={name}
                    fullWidth
                    label={t(keysData[name]?.label)}
                    value={formik.values[name] ?? 'None'}
                    name={name}
                    type="number"
                    size="small"
                    select
                    disabled={disabled}
                    onChange={(event) => {
                      const value =
                        event.target.value === 'None'
                          ? null
                          : event.target.value;
                      formik.setValues({...formik.values, [name]: value});
                    }}
                  >
                    {['None', 10, 15, 30, 60, 120].map((i) => (
                      <MenuItem key={i ?? ''} value={i ?? ''}>
                        {i ?? 'None'}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Grid>
            </Grid>

            <Grid container spacing={4} mb={3}>
              {FIELDS_BY_SECTIONS.sound.map((name) => (
                <Grid key={name} item xs={6}>
                  <SoundSelect
                    fullWidth
                    label={t(keysData[name]?.label)}
                    value={formik.values[name] ?? ''}
                    name={name}
                    size="small"
                    disabled={disabled}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6} xl={4}>
          <Paper sx={{p: 3, mb: 3}}>
            <Box fontSize={20} lineHeight={1.6} mb={2}>
              BELT Color
            </Box>

            <Grid container spacing={4} mb={3}>
              {FIELDS_BY_SECTIONS.color.map((name) => (
                <Grid key={name} item xs={6}>
                  <ColorSelect
                    fullWidth
                    size="small"
                    label={t(keysData[name]?.label)}
                    value={formik.values[name] ?? ''}
                    name={name}
                    disabled={disabled}
                    icons={COLORED_ICONS.belts}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
});
